import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

import App from './App'
import { AnalyticsErrorBoundary, initAnalytics, installGlobalCrashHandlers } from '@/lib/analytics'
import { AuthProvider } from '@/lib/auth/AuthProvider'
import { registerServiceWorker } from '@/lib/push/sw-register'
import './index.css'

// Best-effort SW registration — fire-and-forget. Failure here doesn't
// gate app boot; only push notifications degrade if the worker can't
// install.
void registerServiceWorker()

// Wire window.onerror + unhandledrejection + visibilitychange handlers
// so any uncaught exception (React error boundaries only catch render
// errors — this covers event handlers, timers, promise rejections) and
// any flow-mid-abandonment is recorded in Firebase Analytics before the
// tab tears down. Idempotent; no-op when Firebase config is missing.
installGlobalCrashHandlers()

// Start Analytics at boot. Every event helper initialises lazily, and every
// caller of one sits behind authentication — so with signups closed nothing
// ever triggered `getAnalytics()` and production sent Google nothing at all.
// Consent Mode defaults to denied inside this call, so no cookie is written
// until the visitor agrees.
initAnalytics()

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Match the Android repository's tolerance: refetch on focus is too
      // chatty for a SPA that backgrounds and foregrounds with tabs all day.
      // Manual invalidation after mutations is the source of truth.
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30_000,
    },
  },
})

const rootEl = document.getElementById('root')
if (!rootEl) throw new Error('Root element #root missing from index.html')

createRoot(rootEl).render(
  <StrictMode>
    <AnalyticsErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <QueryClientProvider client={queryClient}>
            <App />
          </QueryClientProvider>
        </AuthProvider>
      </BrowserRouter>
    </AnalyticsErrorBoundary>
  </StrictMode>,
)
